import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { Chart, IChart } from '../Metrics/Chart';
import { strings } from '../../constants/strings';
import { IAdvancedContest, IUserSummary } from '../../types/global/helper';
import { checkContestStatus } from './helper';
import { CustomizedSwitches } from '../Switch/Switch';
import { FullScreenMoreMenu } from '../More/FullScreenMoreMenu';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { IosShare } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getS3images } from '../../redux/contest/actions';
import { useDispatch, useSelector } from 'react-redux';
import { globalConfigurationSelector, s3ImageSelector } from '../../selectors/selectors';
import { SendCampaignEmailModalContainer } from '../ScreenContainer/SendCampaignEmailModalContainer/SendCampaignEmailModalContainer';
import { UserGlobalConfiguration } from '../../utils/UserGlobalConfig';
import { VideoManagementDemo } from '../VideoManagement/';
import { IVideoManager } from '../VideoManagement/VideoManagementDemo';

export interface IContestStats extends IChart {
    contest: IAdvancedContest;
    onClickSendCode?: () => void;
    onClickAssignJuryDuty?: () => void;
    canSendWinnerEmail?: boolean;
    onSendWinnersEmail?: (is_test?: boolean) => void;
    onHostEntryClicked?: () => void;
    onClose?: () => void;
    onSendContestEmail?: (formData: IFormState) => void;
}

export interface IFormState {
    subject?: string;
    image?: string;
    message?: string;
    testUsers?: IUserSummary[];
    type?: string[];
}

const EsContestSubmission: React.FC<IContestStats> = ({
    contest,
    handleFilterSelect,
    onClickSendCode,
    onClickAssignJuryDuty,
    canSendWinnerEmail,
    onSendWinnersEmail,
    onHostEntryClicked,
    onSendContestEmail,
}) => {
    const [submissionTrend, setSubmissionTrend] = useState(false);
    const [emailWarn, setEmailWarn] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const isInJudgePhase = true; // Check if phase is judging
    const ref = useRef<IVideoManager | null>(null);

    const { themeStyle } = useThemeContext();
    const { total_votes, total_submission, total_vote_counted, kind, registration, total_registration_counted } =
        contest || {};
    const styles = dynamicStyles(themeStyle);
    const dispatch = useDispatch();
    const s3Images = useSelector(s3ImageSelector);
    const globalSelector = useSelector(globalConfigurationSelector);
    const isContestEmailButtonEnabled = UserGlobalConfiguration.IsFeatureEnabled(
        globalSelector?.features,
        'contest_emails',
    );

    const handleGetS3Images = useCallback(() => {
        dispatch(getS3images());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleGetS3Images();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleMoreItemClick = (moreType: string) => {
        if (moreType.toLowerCase() === 'send') {
            onClickSendCode && onClickSendCode();
        }

        if (moreType.toLowerCase() === 'manage') {
            onClickAssignJuryDuty && onClickAssignJuryDuty();
        }

        if (moreType.toLowerCase() === 'award') {
            /** Copy this link or share it or something */
            onClickAssignJuryDuty && onClickAssignJuryDuty();
        }

        if (moreType.toLowerCase() === 'email') {
            setEmailWarn(!emailWarn);
        }

        if (moreType.toLowerCase() === 'entry') {
            onHostEntryClicked && onHostEntryClicked();
        }
        if (moreType.toLowerCase() === 'open') {
            setFormOpen(!formOpen);
        }
        if (moreType.toLowerCase() === 'preselect') {
            ref.current?.openModal();
        }
    };

    const onSendRealEmail = (is_test?: boolean) => {
        onSendWinnersEmail && onSendWinnersEmail(is_test);
        setEmailWarn(!emailWarn);
    };

    const handleOnSubmitContestEmailDetails = (formData: IFormState) => {
        setFormOpen(!formOpen);
        onSendContestEmail && onSendContestEmail(formData);
    };

    const premiumMenu = {
        id: 'send',
        title: 'Send Submission Code',
    };
    const sendContestEmailMenu = {
        id: 'email',
        title: 'Send Winners Email',
    };
    const preselectMenu = {
        id: 'preselect',
        title: 'Official Selection Menu',
    };

    const menu = [
        {
            id: 'manage',
            title: 'Manage Jury & Submission',
        },
        {
            id: 'award',
            title: 'Award Prizes',
        },
        {
            id: 'entry',
            title: 'Host Entry',
        },
        isContestEmailButtonEnabled && {
            id: 'open',
            title: 'Send Campaign Emails',
        },
        contest.premium && premiumMenu,
        canSendWinnerEmail && sendContestEmailMenu,
        isInJudgePhase && preselectMenu,
    ];

    const handleOnDownloadClick = () => {
        if (registration) {
            const dataMap = registration.map(d => d.additional_metadata);
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(dataMap);
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
            const excelBuffer = XLSX.write(workbook, {
                bookType: 'xlsx',
                type: 'array',
            });

            const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
            saveAs(blob, `${contest.title}registration-data.xlsx`);
        }
    };

    return (
        <Box style={styles.box} sx={{ gap: '25px' }}>
            <Box style={styles.flexColumn} sx={{ gap: '25px', width: '100%' }}>
                <Stack direction="row" width={'100%'} justifyContent="space-between" alignItems="center">
                    <Typography style={styles.title}>{strings.statistics}</Typography>
                    <FullScreenMoreMenu
                        menu={menu as any}
                        onClick={handleMoreItemClick}
                        title={contest.title}
                        customMoreIcon={<Typography style={styles.code}>Manage Contest</Typography>}
                    />
                </Stack>
                <DialogueSheet
                    onClose={() => setEmailWarn(!emailWarn)}
                    open={emailWarn}
                    onAgreeClicked={() => onSendRealEmail(true)}
                    onDisagreeClick={() => onSendRealEmail(false)}
                    negativeText="No, Send To Real Users"
                    positiveText="Yes, Send As test"
                    header="Send Conclusion Email?"
                    body="You can test this email by first sending it to yourself."
                />

                <VideoManagementDemo contestId={contest._id} ref={ref} />

                <SendCampaignEmailModalContainer
                    onSubmit={handleOnSubmitContestEmailDetails}
                    open={formOpen}
                    onClose={() => {
                        setFormOpen(!formOpen);
                    }}
                    emailImages={s3Images ?? []}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'center', sm: 'flex-start' },
                        justifyContent: 'center',
                        gap: '16px',
                        width: '100%',
                    }}>
                    {kind === 'physical' && total_registration_counted && (
                        <Box
                            style={styles.flexColumn}
                            sx={{
                                gap: '8px',
                                padding: '25px',
                                backgroundColor: theme[themeStyle].gray_text,
                                borderRadius: '10px',
                                width: '100%',
                                height: 'fit-content',
                                position: 'relative',
                            }}>
                            <Typography style={styles.textSm}>{strings.total_no_of_registration}</Typography>
                            <Typography style={styles.numbers}>{total_registration_counted}</Typography>
                            <IconButton
                                onClick={handleOnDownloadClick}
                                sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                }}>
                                <IosShare
                                    sx={{
                                        color: theme[themeStyle].darkGray,
                                        fontSize: 24,
                                    }}
                                />
                            </IconButton>
                        </Box>
                    )}
                    <Box
                        style={styles.flexColumn}
                        sx={{
                            gap: '8px',
                            padding: '25px',
                            backgroundColor: theme[themeStyle].gray_text,
                            borderRadius: '10px',
                            width: '100%',
                            height: 'fit-content',
                        }}>
                        <Typography style={styles.textSm}>{strings.total_no_of_votes}</Typography>
                        <Typography style={styles.numbers}>{total_vote_counted ?? total_votes}</Typography>
                    </Box>
                    <Box
                        style={styles.flexColumn}
                        sx={{
                            gap: '8px',
                            padding: '25px',
                            backgroundColor: theme[themeStyle].gray_text,
                            borderRadius: '10px',
                            width: '100%',
                            height: 'fit-content',
                        }}>
                        <Typography style={styles.textSm}>{strings.submissions}</Typography>
                        <Typography style={styles.numbers}>{total_submission}</Typography>
                    </Box>
                    <Box
                        style={styles.flexColumn}
                        sx={{
                            gap: '8px',
                            padding: '25px',
                            backgroundColor: theme[themeStyle].gray_text,
                            borderRadius: '10px',
                            width: '100%',
                            height: 'fit-content',
                        }}>
                        <Typography style={styles.textSm}>{strings.duration}</Typography>
                        <Typography style={styles.numbers}>{checkContestStatus(contest)}</Typography>
                    </Box>
                </Box>
            </Box>
            <CustomizedSwitches
                label="Submission Trend"
                switchProps={{
                    color: 'primary',
                    centerRipple: true,
                    checked: submissionTrend,
                    value: submissionTrend,
                    onChange: () => setSubmissionTrend(!submissionTrend),
                }}
            />

            <Chart
                contest={contest}
                trendSelector={submissionTrend ? 'submission' : 'vote'}
                submissionStat={contest.submission_stats}
                voteStat={contest.vote_stats}
                handleFilterSelect={handleFilterSelect}
            />
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        box: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            position: 'relative',
            height: 'fit-content',
            width: '100%',
        },
        numbers: {
            fontSize: 20,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            color: theme[colorScheme].filters_white,
            width: '100%',
            textTransform: 'capitalize',
        },
        textSm: {
            fontSize: 12,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_REGULAR,
            color: theme[colorScheme].form_label,
            width: '100%',
        },
        title: {
            fontSize: 24,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[colorScheme].filters_white,
        },
        code: {
            fontSize: 10,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[colorScheme].black,
            backgroundColor: theme[colorScheme].highlight,
            padding: 10,
            borderRadius: 5,
            cursor: 'pointer',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
        },
        flexRowBetween: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        },
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
    });

export { EsContestSubmission };
